import { FilterBase } from "@components/commerce/product-listing/filters/common/filter/FilterBase";
import { SizeSwitcherDropdown } from "@components/commerce/product-listing/filters/common/size-switcher/SizeSwitcher";
import { FiltersContext } from "@components/commerce/product-listing/filters/context/FiltersContext";
import SizeGuide from "@components/ui/size-guide-modal/SizeGuide";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import type { SizeLocales } from "@lib/constants/filtersConstants";
import { FEMALE, MALE } from "@lib/constants/personCharacteristics";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { useStore } from "@store/index";
import { Button } from "@ui/components/forms/button/Button";
import { Box } from "@ui/components/layout/box/Box";
import { Flex } from "@ui/components/layout/flex/Flex";
import { SelectDropdownItem } from "@ui/components/overlay/select-dropdown/SelectDropdown";
import type { RefinementListItem } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import { memo, useContext, useEffect } from "react";
import { useRefinementList } from "react-instantsearch";
import { styles } from "./SizeToolbarFilterStyles";

export const SizeToolbarFilter = memo(
    ({
        filterName,
        isToolbar,
        isLoading,
    }: {
        filterName: string;
        isToolbar: boolean;
        isLoading: boolean;
    }) => {
        const initialFilters = useStore((state) => state.data.products?.initialFilters);
        const setLastLoadedPage = useStore((state) => state.setLastLoadedPage);
        const defaultShoeSize = useStore((state) => state.data.me.globalShoeSizeConvention);
        const setDefaultShoeSize = useStore((state) => state.setGlobalShoeSizeConvention);

        const { isMenPage, facets, setFilterItems } = useContext(FiltersContext);

        const { items, refine } = useRefinementList({
            attribute: `fullSizes.${defaultShoeSize.toLowerCase()}`,
            operator: "or",
            sortBy: ["name"],
            limit: 100,
        });

        useEffect(() => {
            items.filter((it) => it.isRefined).forEach((it) => refine(it.value));
        }, [defaultShoeSize]);

        useEffect(() => {
            const newState = facets;
            newState[filterName] = { items, refine };
            setFilterItems({ ...newState });
            //eslint-disable-next-line react-hooks/exhaustive-deps
        }, [items]);

        let timerId = undefined;

        const applyRefinement = (item) => {
            if (timerId) {
                clearTimeout(timerId);
            } else {
                timerId = setTimeout(() => {
                    AnalyticsClient.listFilterApplied(
                        filterName,
                        item,
                        `fullSizes.${defaultShoeSize.toLowerCase()}`
                    );
                }, 1000);
            }
            scrollTo({ top: 0 });
            setLastLoadedPage(1);
            refine(item);
        };

        if (!isToolbar) {
            return <></>;
        }

        const switchSizeLocale = (newSizeLocale: SizeLocales) => {
            scrollTo({ top: 0 });
            setLastLoadedPage(1);
            setDefaultShoeSize(newSizeLocale);
        };

        const dropdownItems = (
            <Box sx={styles.itemsWrapper}>
                <SizeSection
                    sizesList={initialFilters[`Size.${defaultShoeSize}`] as RefinementListItem[]}
                    setSizeLocale={switchSizeLocale}
                    sizeLocale={defaultShoeSize}
                    items={items}
                    refine={applyRefinement}
                />
                <Flex width="96%" margin="auto" justifyContent={"flex-start"}>
                    <SizeGuide
                        isToolbar={true}
                        gender={isMenPage ? MALE : FEMALE}
                        type={ModalTypes.globalSizeGuide}
                    />
                </Flex>
            </Box>
        );

        return (
            <FilterBase
                isLoading={isLoading}
                dropdownItems={dropdownItems}
                filterName={filterName}
                isToolbar={isToolbar}
            />
        );
    }
);

SizeToolbarFilter.displayName = "SizeToolbarFilter";

const SizeSection = ({ sizesList = [], items, refine, sizeLocale, setSizeLocale }) => {
    return (
        <Flex
            sx={{
                ...styles.sizeSectionMainContainer,
            }}
        >
            {sizesList.length ? (
                <Box sx={styles.sectionWrapper}>
                    <SizeSwitcherDropdown
                        sizeLocale={sizeLocale}
                        setSizeLocale={(label) => {
                            setSizeLocale(label);
                        }}
                    />
                    <Box sx={styles.sizesContainer}>
                        {[...sizesList]
                            .sort((a, b) => {
                                return parseInt(a.label) - parseInt(b.label);
                            })
                            .map((item) => {
                                const dynamicItem = items.find((it) => it.value === item.value);
                                return (
                                    <SelectDropdownItem
                                        key={item.label}
                                        as={Box}
                                        sx={{
                                            ...styles.selectDropdownItem,
                                        }}
                                        isDisabled={!dynamicItem}
                                        onClick={() => {
                                            refine(item.value);
                                        }}
                                    >
                                        <Button
                                            key={item.label}
                                            sx={{
                                                ...styles.itemButton,
                                                _disabled: {
                                                    bg: "rgba(238, 238, 238, .3)",
                                                    color: "rgba(0, 0, 0, .6)",
                                                },
                                            }}
                                            size="sm"
                                            fontWeight="bold"
                                            isDisabled={!dynamicItem}
                                            variant={dynamicItem?.isRefined ? "solid" : "standard"}
                                        >
                                            <span>{item.label}</span>
                                        </Button>
                                    </SelectDropdownItem>
                                );
                            })}
                    </Box>
                </Box>
            ) : null}
        </Flex>
    );
};
